'use client';

import Link from 'next/link';
import { ExternalLink, Clipboard, Check } from 'lucide-react';
import { useState, useMemo, Fragment } from 'react';
import type { CatalogItem, CatalogType } from '@/lib/services/catalog/catalog-types';

interface TableViewProps {
  artworks: CatalogItem[];
  onMouseEnter?: (artwork: CatalogItem) => void;
  onMouseLeave?: () => void;
  onMouseMove?: (e: React.MouseEvent) => void;
  onCopyWallet?: (walletAddress: string | undefined) => void;
  copiedWallet?: string | null;
  tableRef?: React.RefObject<HTMLDivElement>;
  catalogType?: CatalogType;
}

// Group items by series and sort by date
interface SeriesGroup {
  type: 'series';
  seriesId: number;
  seriesTitle: string;
  seriesHref: string | null;
  seriesYear: number | null;
  artworks: CatalogItem[];
  sortDate: string;
}

interface StandaloneItem {
  type: 'standalone';
  item: CatalogItem;
  sortDate: string;
}

type TreeItem = SeriesGroup | StandaloneItem;

function groupAndSortItems(items: CatalogItem[]): TreeItem[] {
  const seriesMap = new Map<number, SeriesGroup>();
  const standaloneItems: StandaloneItem[] = [];

  // Group items by seriesId
  for (const item of items) {
    if (item.seriesId && item.seriesTitle) {
      const existing = seriesMap.get(item.seriesId);
      if (existing) {
        existing.artworks.push(item);
        // Update sortDate to earliest
        if (item.date && item.date < existing.sortDate) {
          existing.sortDate = item.date;
        }
      } else {
        seriesMap.set(item.seriesId, {
          type: 'series',
          seriesId: item.seriesId,
          seriesTitle: item.seriesTitle,
          seriesHref: item.seriesHref || null,
          seriesYear: item.seriesYear || null,
          artworks: [item],
          sortDate: item.date || '',
        });
      }
    } else {
      standaloneItems.push({
        type: 'standalone',
        item,
        sortDate: item.date || '',
      });
    }
  }

  // Convert map to array and combine with standalone
  const allItems: TreeItem[] = [...seriesMap.values(), ...standaloneItems];

  // Sort by date (newest first)
  allItems.sort((a, b) => {
    const dateA = a.sortDate || '';
    const dateB = b.sortDate || '';
    return dateB.localeCompare(dateA);
  });

  // Sort artworks within each series by date
  for (const item of allItems) {
    if (item.type === 'series') {
      item.artworks.sort((a, b) => {
        const dateA = a.date || '';
        const dateB = b.date || '';
        return dateB.localeCompare(dateA);
      });
    }
  }

  return allItems;
}

export function TableView({
  artworks,
  onMouseEnter,
  onMouseLeave,
  onMouseMove,
  onCopyWallet,
  copiedWallet: externalCopiedWallet,
  tableRef,
  catalogType = 'all',
}: TableViewProps) {
  // Use internal state for copied wallet if external is not provided
  const [internalCopiedWallet, setInternalCopiedWallet] = useState<string | null>(null);
  const copiedWallet = externalCopiedWallet !== undefined ? externalCopiedWallet : internalCopiedWallet;

  const copyToClipboard = (wallet: string | undefined) => {
    if (!wallet) return;

    if (onCopyWallet) {
      onCopyWallet(wallet);
    } else {
      navigator.clipboard.writeText(wallet);
      setInternalCopiedWallet(wallet);
      setTimeout(() => setInternalCopiedWallet(null), 2000);
    }
  };

  // Group items into tree structure (only for main catalog page)
  const useTreeView = catalogType === 'all';
  const treeItems = useMemo(
    () => (useTreeView ? groupAndSortItems(artworks) : null),
    [artworks, useTreeView]
  );

  // Katalog tipine göre ek sütun gösterme
  const showCatalogTypeColumn = catalogType === 'all';

  // Render artwork row
  const renderArtworkRow = (
    artwork: CatalogItem,
    index: number,
    isLast: boolean,
    isInSeries: boolean
  ) => {
    const treePrefix = isInSeries ? (isLast ? '└─' : '├─') : '•';

    return (
      <tr
        key={`${artwork.id}-${index}`}
        className="hover:bg-zinc-50"
        onMouseEnter={onMouseEnter ? () => onMouseEnter(artwork) : undefined}
        onMouseLeave={onMouseLeave}
        onMouseMove={onMouseMove}
      >
        <td className="px-3 py-2 text-[14px] whitespace-nowrap">
          <span className="font-mono text-[12px]">
            <span className={`text-zinc-400 ${isInSeries ? 'pl-4' : ''}`}>{treePrefix} </span>
            <Link href={artwork.href || '#'} className="underline text-zinc-700 hover:text-zinc-900 cursor-pointer">
              {artwork.title}
            </Link>
          </span>
        </td>
        <td className="px-3 py-2 text-[14px] whitespace-nowrap">
          <span className="font-mono text-[12px]">{artwork.year}</span>
        </td>
        <td className="px-3 py-2 text-[14px]">
          <div className="flex flex-nowrap gap-1">
            {Array.isArray(artwork.medium) ? (
              artwork.medium.map(med => (
                <span key={med} className="text-[10px] bg-zinc-100 px-2 py-0.5 font-mono whitespace-nowrap">
                  {med}
                </span>
              ))
            ) : (
              <span className="text-[10px] bg-zinc-100 px-2 py-0.5 font-mono whitespace-nowrap">{artwork.medium || 'Unknown'}</span>
            )}
          </div>
        </td>
        <td className="px-3 py-2 text-[14px]">
          <div className="flex flex-nowrap gap-1">
            {Array.isArray(artwork.tags) ? (
              artwork.tags.map(tag => (
                <span key={tag} className="text-[10px] bg-zinc-100 px-2 py-0.5 font-mono whitespace-nowrap">
                  {tag}
                </span>
              ))
            ) : (
              <span className="text-[10px] bg-zinc-100 px-2 py-0.5 font-mono whitespace-nowrap">{artwork.tags || 'Unknown'}</span>
            )}
          </div>
        </td>
        {showCatalogTypeColumn && catalogType === 'all' && (
          <td className="px-3 py-2 text-[14px]">
            <span className="text-[10px] bg-zinc-100 px-2 py-0.5 font-mono">
              {artwork.type || catalogType.toUpperCase()}
            </span>
          </td>
        )}
        <td className="px-3 py-2 text-[14px]">
          {artwork.walletAddress ? (
            <div className="flex items-center">
              <span className="font-mono text-[12px] truncate w-24" title={artwork.walletAddress}>
                {artwork.walletAddress.slice(0, 6)}...
                {artwork.walletAddress.slice(-4)}
              </span>
              <button
                onClick={() => copyToClipboard(artwork.walletAddress)}
                className="ml-2 text-zinc-500 hover:text-zinc-700"
                aria-label="Copy to clipboard"
              >
                {copiedWallet === artwork.walletAddress ? (
                  <Check className="h-3 w-3 text-zinc-600" />
                ) : (
                  <Clipboard className="h-3 w-3" />
                )}
              </button>
              <a
                href={`https://etherscan.io/address/${artwork.walletAddress}`}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-2 text-zinc-500 hover:text-zinc-700"
                aria-label="View on Etherscan"
              >
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          ) : (
            <span className="font-mono text-[12px] text-gray-400">Not available</span>
          )}
        </td>
        <td className="px-3 py-2 text-[14px]">
          <Link
            href={artwork.href || '#'}
            className="text-[12px] font-mono underline text-zinc-700 hover:text-zinc-900 cursor-pointer"
          >
            VIEW
          </Link>
        </td>
      </tr>
    );
  };

  // Render series header row
  const renderSeriesRow = (series: SeriesGroup) => {
    return (
      <tr key={`series-${series.seriesId}`} className="bg-zinc-50/50">
        <td className="px-3 py-2 text-[14px]" colSpan={showCatalogTypeColumn ? 7 : 6}>
          <span className="font-mono text-[12px]">
            <span className="text-zinc-500">■ </span>
            {series.seriesHref ? (
              <Link href={series.seriesHref} className="font-medium text-zinc-800 hover:text-zinc-900 underline cursor-pointer">
                {series.seriesTitle}
              </Link>
            ) : (
              <span className="font-medium text-zinc-800">{series.seriesTitle}</span>
            )}
            <span className="text-zinc-500 ml-3">
              {series.seriesYear && `${series.seriesYear} · `}
              {series.artworks.length} {series.artworks.length === 1 ? 'work' : 'works'}
            </span>
          </span>
        </td>
      </tr>
    );
  };

  return (
    <div className="overflow-auto" ref={tableRef}>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-zinc-50">
          <tr>
            <th scope="col" className="px-3 py-2 text-left text-[12px] font-mono uppercase text-zinc-700 tracking-wider">
              Title
            </th>
            <th scope="col" className="px-3 py-2 text-left text-[12px] font-mono uppercase text-zinc-700 tracking-wider">
              Year
            </th>
            <th scope="col" className="px-3 py-2 text-left text-[12px] font-mono uppercase text-zinc-700 tracking-wider">
              Medium
            </th>
            <th scope="col" className="px-3 py-2 text-left text-[12px] font-mono uppercase text-zinc-700 tracking-wider">
              Tags
            </th>
            {showCatalogTypeColumn && (
              <th scope="col" className="px-3 py-2 text-left text-[12px] font-mono uppercase text-zinc-700 tracking-wider">
                Type
              </th>
            )}
            <th scope="col" className="px-3 py-2 text-left text-[12px] font-mono uppercase text-zinc-700 tracking-wider">
              Wallet
            </th>
            <th scope="col" className="px-3 py-2 text-left text-[12px] font-mono uppercase text-zinc-700 tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {useTreeView && treeItems
            ? treeItems.map((treeItem, treeIndex) => {
                if (treeItem.type === 'series') {
                  return (
                    <Fragment key={`series-group-${treeItem.seriesId}`}>
                      {renderSeriesRow(treeItem)}
                      {treeItem.artworks.map((artwork, artworkIndex) =>
                        renderArtworkRow(
                          artwork,
                          artworkIndex,
                          artworkIndex === treeItem.artworks.length - 1,
                          true
                        )
                      )}
                    </Fragment>
                  );
                } else {
                  return renderArtworkRow(treeItem.item, treeIndex, false, false);
                }
              })
            : artworks.map((artwork, index) => (
                <tr
                  key={`${artwork.id}-${index}`}
                  className="hover:bg-zinc-50"
                  onMouseEnter={onMouseEnter ? () => onMouseEnter(artwork) : undefined}
                  onMouseLeave={onMouseLeave}
                  onMouseMove={onMouseMove}
                >
                  <td className="px-3 py-2 text-[14px]">
                    <span className="font-mono text-[12px]">
                      <Link href={artwork.href || '#'} className="underline text-zinc-700 hover:text-zinc-900 cursor-pointer">
                        {artwork.title}
                      </Link>
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[14px]">
                    <span className="font-mono text-[12px]">{artwork.year}</span>
                  </td>
                  <td className="px-3 py-2 text-[14px]">
                    <div className="flex flex-wrap gap-1">
                      {Array.isArray(artwork.medium) ? (
                        artwork.medium.map(med => (
                          <span key={med} className="text-[10px] bg-zinc-100 px-2 py-0.5 font-mono">
                            {med}
                          </span>
                        ))
                      ) : (
                        <span className="text-[10px] bg-zinc-100 px-2 py-0.5 font-mono">{artwork.medium || 'Unknown'}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-[14px]">
                    <div className="flex flex-wrap gap-1">
                      {Array.isArray(artwork.tags) ? (
                        artwork.tags.map(tag => (
                          <span key={tag} className="text-[10px] bg-zinc-100 px-2 py-0.5 font-mono">
                            {tag}
                          </span>
                        ))
                      ) : (
                        <span className="text-[10px] bg-zinc-100 px-2 py-0.5 font-mono">{artwork.tags || 'Unknown'}</span>
                      )}
                    </div>
                  </td>
                  {showCatalogTypeColumn && (
                    <td className="px-3 py-2 text-[14px]">
                      <span className="text-[10px] bg-zinc-100 px-2 py-0.5 font-mono">
                        {artwork.type || catalogType.toUpperCase()}
                      </span>
                    </td>
                  )}
                  <td className="px-3 py-2 text-[14px]">
                    {artwork.walletAddress ? (
                      <div className="flex items-center">
                        <span className="font-mono text-[12px] truncate w-24" title={artwork.walletAddress}>
                          {artwork.walletAddress.slice(0, 6)}...
                          {artwork.walletAddress.slice(-4)}
                        </span>
                        <button
                          onClick={() => copyToClipboard(artwork.walletAddress)}
                          className="ml-2 text-zinc-500 hover:text-zinc-700"
                          aria-label="Copy to clipboard"
                        >
                          {copiedWallet === artwork.walletAddress ? (
                            <Check className="h-3 w-3 text-zinc-600" />
                          ) : (
                            <Clipboard className="h-3 w-3" />
                          )}
                        </button>
                        <a
                          href={`https://etherscan.io/address/${artwork.walletAddress}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 text-zinc-500 hover:text-zinc-700"
                          aria-label="View on Etherscan"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    ) : (
                      <span className="font-mono text-[12px] text-gray-400">Not available</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[14px]">
                    <Link
                      href={artwork.href || '#'}
                      className="text-[12px] font-mono underline text-zinc-700 hover:text-zinc-900 cursor-pointer"
                    >
                      VIEW
                    </Link>
                  </td>
                </tr>
              ))}
        </tbody>
      </table>
    </div>
  );
}
